import { ReferralCode, ReferralUse } from '@/types/referral';
import { StandardResponse } from '@/types/response';
import { UpdateUserDto, User } from '@/types/user';

import AppApi from './app-api';

class UserApi {
  constructor(private api: AppApi) {}

  async getUserMe(): Promise<User> {
    const res = await this.api.INSTANCE.get('/user/me');
    return res.data;
  }

  async patchUser(body: UpdateUserDto): Promise<User> {
    const res = await this.api.INSTANCE.patch('/user/me', body);
    return res.data;
  }

  async checkPassword(body: { password: string }): Promise<boolean> {
    const res = await this.api.INSTANCE.post('/user/check-password', body);

    return res.data;
  }

  async deleteUser(): Promise<void> {
    await this.api.INSTANCE.delete('/user/me');
  }

  async updateBusinessNumber(body: {
    business_number: string;
  }): Promise<{ business_number: string }> {
    const res = await this.api.INSTANCE.patch('/user/business-number', body);
    return res.data;
  }

  async updatePrimaryShop({ userId, shopId }: { userId: string; shopId: string }): Promise<User> {
    const res = await this.api.INSTANCE.patch(`/user/${userId}/primary-shop`, {
      shop_id: shopId,
    });

    return res.data as User;
  }

  async updateVerifiedPhoneNumber({
    phoneNumber,
    code,
    user_id,
  }: {
    phoneNumber: string;
    code: string;
    user_id: string;
  }) {
    try {
      const res = await this.api.INSTANCE.patch(`/user/${user_id}/verify/phone-number`, {
        phone_number: phoneNumber,
        code: Number(code),
      });
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async requestPhoneVerifyCode({
    phoneNumber,
    user_id,
  }: {
    phoneNumber: string;
    user_id: string;
  }): Promise<{ message: string }> {
    try {
      const res = await this.api.INSTANCE.post(`/user/${user_id}/verify/phone-number/request`, {
        phone_number: phoneNumber,
      });

      return res.data as { message: string };
    } catch (e) {
      throw e;
    }
  }

  async registerNegativeReviewNumber({
    phoneNumber,
    code,
    user_id,
  }: {
    phoneNumber: string;
    code: string;
    user_id: string;
  }) {
    try {
      const res = await this.api.INSTANCE.patch(`/user/${user_id}/verify/phone-number`, {
        phone_number: phoneNumber,
        code,
      });
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async registerNegativeReview({ to, isActive, userId }: RegisterNegativeReviewNumberAnnotation) {
    try {
      const res = await this.api.INSTANCE.post(`/alarm-talk/negative-review/register`, {
        to,
        is_active: isActive,
        user_id: userId,
      });
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async updateNegativeReviewAlarm({
    to,
    isActive,
    userId,
  }: RegisterNegativeReviewNumberAnnotation) {
    try {
      const obj = {
        user_id: userId,
        phone_number: to,
        negative_review_alarm: isActive,
      };
      const res = await this.api.INSTANCE.patch(
        `/alarm-talk/user/${userId}/negative-review-alarm/update`,
        obj,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async getVerifiedPhoneNumbers({ userId }: { userId: string }): Promise<UserPhoneNumber[]> {
    try {
      const res = await this.api.INSTANCE.get(`/user/${userId}/verify/phone-number`);
      return res.data as UserPhoneNumber[];
    } catch (e) {
      throw e;
    }
  }

  async getNegativeReviewAlarm({ userId }: { userId: string }): Promise<{ is_active: boolean }> {
    try {
      const res = await this.api.INSTANCE.get(`/alarm-talk/user/${userId}/negative-review-alarm`);
      const data = res.data as StandardResponse<NegativeReviewAlarmStatus>;

      return data.data;
    } catch (e: any) {
      console.log(e);
      throw e;
    }
  }

  async getOwnReferralCode({ userId }: { userId: string }): Promise<ReferralCode> {
    try {
      const res = await this.api.INSTANCE.get(`/user/${userId}/referral-code`);
      return res.data as ReferralCode;
    } catch (e: any) {
      console.log(e);
      throw e;
    }
  }

  async createReferralCode({ userId }: { userId: string }): Promise<ReferralCode> {
    try {
      const res = await this.api.INSTANCE.post(`/user/${userId}/referral-code`);
      return res.data as ReferralCode;
    } catch (e: any) {
      console.log(e);
      throw e;
    }
  }

  async getOwnReferralUse({ userId }: { userId: string }): Promise<ReferralUse> {
    try {
      const res = await this.api.INSTANCE.get(`/user/${userId}/referral-use`);
      return res.data as ReferralUse;
    } catch (e: any) {
      console.log(e);
      throw e;
    }
  }
}

type RegisterNegativeReviewNumberAnnotation = {
  to: string;
  isActive: boolean;
  userId: string;
};

export type UserPhoneNumber = {
  user_phone_number_id: number;
  phone_number: string;
  created_at: Date;
  updated_at: Date;
};

export type NegativeReviewAlarmStatus = {
  negative_review_alarm_id: string;
  is_active: boolean;
  created_at: Date;
  phone_number: string;
  updated_at: Date;
};

export default UserApi;
